import React from 'react';

import { facilitiesURL } from '@admin/config/routes';
import { FacilityServiceTypeUpdateInput } from '@admin/schema';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Field } from '@admin/styled/field';
import { FieldHint } from '@admin/styled/field_hint';
import { FieldInput } from '@admin/styled/field_input';
import { FieldLabel } from '@admin/styled/field_label';
import { FieldTextArea } from '@admin/styled/field_text_area';
import { FormBody } from '@admin/styled/form_body';
import { FormCard } from '@admin/styled/form_card';
import { FormFooter } from '@admin/styled/form_footer';

export type FacilityServiceTypeFormValues = FacilityServiceTypeUpdateInput & {
  slug: string;
};

export const ServiceTypeForm: React.FC<{
  initialValues: FacilityServiceTypeFormValues;
  onSubmit(values: FacilityServiceTypeUpdateInput): void;
  onCancel?(): void;
  saving?: boolean;
}> = ({ initialValues, onSubmit, onCancel, saving }) => {
  const [values, setValues] = React.useState(initialValues);

  const canSave =
    values.name.trim().length > 0 && values.description.trim().length > 0 && Number.isFinite(values.position);

  return (
    <FormCard>
      <FormBody>
        <Field>
          <FieldLabel htmlFor="facility_service_type_slug">Slug</FieldLabel>
          <FieldHint>Stable identifier used in code; cannot be changed.</FieldHint>
          <FieldInput type="text" id="facility_service_type_slug" value={values.slug} disabled readOnly />
        </Field>

        <Field>
          <FieldLabel htmlFor="facility_service_type_name">Display name</FieldLabel>
          <FieldInput
            type="text"
            id="facility_service_type_name"
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            autoFocus
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="facility_service_type_position">Sort order</FieldLabel>
          <FieldHint>Lower numbers appear first in admin lists.</FieldHint>
          <FieldInput
            type="number"
            id="facility_service_type_position"
            value={values.position}
            onChange={(e) => setValues({ ...values, position: e.target.valueAsNumber })}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="facility_service_type_description">Description</FieldLabel>
          <FieldHint>Shown in Settings and used to explain what this service type means operationally.</FieldHint>
          <FieldTextArea
            id="facility_service_type_description"
            value={values.description}
            onChange={(e) => setValues({ ...values, description: e.target.value })}
          />
        </Field>
      </FormBody>
      <FormFooter>
        {onCancel ? (
          <Button variant="secondary" as="button" type="button" onClick={onCancel}>
            Cancel
          </Button>
        ) : (
          <ButtonLink variant="secondary" to={facilitiesURL()}>
            Cancel
          </ButtonLink>
        )}
        <Button
          variant="primary"
          type="button"
          disabled={!canSave || saving}
          onClick={() =>
            onSubmit({
              name: values.name.trim(),
              description: values.description.trim(),
              position: values.position,
            })
          }
        >
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </FormFooter>
    </FormCard>
  );
};
